"use client"
import Script from 'next/script';
import Link from 'next/link';
import { track } from '@vercel/analytics';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { trackMetaEvent, generateEventId } from '@/utils/meta-pixel';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { classNames } from '@/lib/utils';
import { useLoggedInUser } from '@/hooks/useLoggedInUser';
import { chargebeeFrontendSettingsForSite } from '@shared-libs/utils/helpers';
import { useUserProfile } from '@/utils/react-query-hooks';
import { presetColorThemes } from '@/lib/utils';
import { SetupLayoutContext } from './SetupLayoutContext';
import Pricing, { plans } from '@/components/landing/LandingPricing';
import { Button } from '@/components/presentational/tailwind/Button';
import InstagramSearchField from '@/components/presentational/tailwind/InstagramSearchField';
import ColorPicker from '@/components/presentational/tailwind/ColorPicker';
import ChargebeePaymentForm from '@/components/presentational/tailwind/ChargebeePaymentForm';
import { LoadingSpinner } from '@/components/presentational/tailwind/LoadingSpinner';
// Icons
import LightningFillIcon from "@public/img/icons/lightning-fill.svg";
import { LockClosedIcon } from '@heroicons/react/20/solid';
import { HiArrowSmRight } from 'react-icons/hi';
import { BiCheckShield } from 'react-icons/bi';
import { FiArrowLeft } from 'react-icons/fi';
// import { motion, AnimatePresence } from 'framer-motion';
// const stepTransitionVariants = {
//   initial: { opacity: 0, y: 50 },
//   enter: { opacity: 1, y: 0 },
//   exit: { opacity: 0, y: -50 },
// };
import '@public/css/pricing.css';
import '@public/css/landing.css';

export function Setup({ userCountry, showChargebeeHostedPaymentForm = false }) {

  const router = useRouter();
  const pathname = usePathname();
  const isLoggedInSetup = pathname.startsWith('/app/start');
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [accountColor, setAccountColor] = useState(presetColorThemes[0]);
  const [instagramProfile, setInstagramProfile] = useState(null);
  const [setupStates, setSetupStates] = useState({
    email: searchParams.get('email') || '',
    password: '',
    instagramUsername: searchParams.get('username') || '',
    activePeriod: 'Yearly' as "Yearly" | "Monthly",
    selectedPlan: plans[0],
  });
  const step = (searchParams.get('step') && setupStates.instagramUsername) ? parseInt(searchParams.get('step')) : 0;
  useEffect(() => {
    if (!isLoggedInSetup) return;
    if (step === 0)
      setSetupStates(prevState => ({ ...prevState, instagramUsername: searchParams.get('username') || '' }));
    if (step !== parseInt(searchParams.get('step')))
      setSearchParams({ step: step.toString() });
  }, [step]);

  useEffect(() => {
    if (searchParams.get('step') === '1' && !setupStates.instagramUsername)
      setSearchParams({ step: '0' });
  }, [step, setupStates.instagramUsername]);

  // KEESY uses a single gateway - no multi-entity routing needed
  const { data: userProfile, isLoading: isProfileLoading } = isLoggedInSetup ? useUserProfile() : { data: null, isLoading: false };
  const chargebeeSite = 'keesy';

  // const supabaseAuthCookie = Object.keys(Object.fromEntries(document.cookie.split('; ').map(cookie => cookie.split('=')))).find(name => /^sb-.+-auth-token$/.test(name));
  // const isLoggedIn = hasCookie('supabase-auth-token') || hasCookie(supabaseAuthCookie);


  const user = isLoggedInSetup ? useLoggedInUser() : null;
  const [cbInstance, setCbInstance] = useState(null);
  useEffect(() => {
    if (!isLoggedInSetup || !chargebeeSite) return;
    if (typeof window !== 'undefined') {
      if (!cbInstance && window.Chargebee) {
        console.log("Initializing Chargebee instance...");
        setCbInstance(
          window.Chargebee.init({
            ...chargebeeFrontendSettingsForSite(chargebeeSite),
            iframeOnly: true,
          })
        );
        return;
      }
    }
  }, [isLoggedInSetup, window, window.Chargebee, cbInstance, chargebeeSite])

  const openChargebeeCheckout = useCallback((planId: string) => {
    console.log('setupStates', setupStates)
    if (typeof window !== 'undefined' && chargebeeSite) {
      if (!cbInstance) {
        if (window.Chargebee.getInstance()) {
          setCbInstance(window.Chargebee.getInstance())
        } else if (window.Chargebee) {
          setCbInstance(
            window.Chargebee.init({
              ...chargebeeFrontendSettingsForSite(chargebeeSite),
              iframeOnly: true,
            })
          );
        }
        return;
      }

      cbInstance?.openCheckout({
        hostedPage: async () => {

          setIsLoading(true)
          let tolt_referral = typeof window !== 'undefined' && (window as any).tolt_referral ? (window as any).tolt_referral : undefined;
          let queryString = {
            planId,
            username: setupStates.instagramUsername,
            userId: user?.id,
            email: user?.email,
            ...(user?.user_metadata?.full_name && { name: user?.user_metadata?.full_name }),
            ...(tolt_referral && { tolt_referral }),
            site: chargebeeSite
          }
          let qs = Object.keys(queryString).map(key => `${key}=${encodeURIComponent(queryString[key])}`).join('&');
          console.log('qs', qs)

          let retryCount = 0;
          while (retryCount < 2) {
            try {
              const data = await (await fetch(`/api/chargebee/get-checkout-link?${qs}`)).json()
              return data;
            } catch (error) {
              retryCount++;
              if (retryCount >= 2) {
                queryString["openLink"] = 'true';
                qs = Object.keys(queryString).map(key => `${key}=${encodeURIComponent(queryString[key])}`).join('&');
                window.location.href = `/api/chargebee/get-checkout-link?${qs}`;
                break;
              }
            }
          }
        },
        success(hostedPageId) {
          console.log("Successfully created/updated subscription")
          setIsLoading(false)

          // Track Purchase event for Meta Pixel with value and eventId for deduplication
          const purchaseValue = setupStates.activePeriod === "Yearly"
            ? setupStates.selectedPlan.price.YearlyTotal
            : (setupStates.selectedPlan.firstMonthMonthlyPrice || setupStates.selectedPlan.price.Monthly);
          const purchaseEventId = generateEventId('purchase', user?.id);
          trackMetaEvent('Purchase', {
            value: purchaseValue,
            content_name: `${planId} Subscription`,
            content_category: 'subscription',
            currency: 'USD'
          }, purchaseEventId);

          // Call /api/chargebee/finish-checkout
          fetch(`/api/chargebee/finish-checkout?site=${chargebeeSite}&hostedPageId=${hostedPageId}&username=${setupStates.instagramUsername}&planId=${planId}`)
            .then(response => {
              if (response.status === 307) {
                window.location.href = response.headers.get('Location');
              } else {
                return response.json();
              }
            })
            .then(data => {
              console.log("data: ", data);
              setIsLoading(false)
              // Redirect to the account page
              window.location.href = (`/app`);
            })
            .catch(error => {
              console.error("Error finishing checkout: ", error);
              setIsLoading(false)
            });
        },
        close: () => {
          console.log("checkout new closed");
          setIsLoading(false)
        },
        step(step) {
          console.log("checkout", step);
          setIsLoading(false)
        },
      });
    }
  }, [router, cbInstance, user, setupStates, setupStates.instagramUsername, chargebeeSite]);




  /*             */
  /* Setup Steps */
  /*             */

  const handleStepOne = useCallback(async () => {
    if (setupStates.instagramUsername.trim() === '' || setupStates.instagramUsername.length < 3) {
      setErrorMessage('Please enter your Instagram username.');
      return false;
    }

    setIsLoading(true);
    console.log(`Fetching Instagram profile @${setupStates.instagramUsername}...`);
    try {

      const profile = await fetch(`/api/instagram/get-profile?username=${setupStates.instagramUsername}`).then(res => res.json());
      // const profile = await Instagram.fetchInstagramProfile(setupStates.instagramUsername, false);
      if (profile) {
        console.log(`Instagram profile @${setupStates.instagramUsername} found!`);
        console.log(profile);
        setInstagramProfile(profile);
        return true;
      } else {
        setErrorMessage(`Instagram account <b>@${setupStates.instagramUsername}</b> not found. Please check the username and try again.`);
        if (typeof document !== 'undefined') {
          const element = document.getElementById('instagram-username');
          if (element) setTimeout(() => element.focus(), 500);
        }
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      setErrorMessage(`An error occurred while fetching your Instagram profile. Please check the username or try again later.`);
      setIsLoading(false);
      return false;
    }
  }, [setErrorMessage, setIsLoading, setInstagramProfile, setupStates.instagramUsername]);

  const handleStepTwo = useCallback(async () => {
    const emailRegex = /^[\w-]+(\.[\w-]+)*(\+[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
    if (!isLoggedInSetup) {
      if (setupStates.email.trim() === '' || !emailRegex.test(setupStates.email)) {
        setErrorMessage('Please enter a valid email address.');
        return false;
      }
      if (setupStates.password.trim() === '' || setupStates.password.length < 6) {
        setErrorMessage('Please enter a valid password.');
        return false;
      }

      setIsLoading(true);
      fetch('/api/accounts/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: setupStates.email,
          password: setupStates.password,
          instagram_username: setupStates.instagramUsername,
          theme_color: accountColor.name,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.error) {
            setErrorMessage(data.error);
            setIsLoading(false);
            return false;
          }
          router.push(`/app/account/${data.account_id}?connected=true`);
        })
        .catch((error) => {
          setErrorMessage(error);
          setIsLoading(false);
          return false;
        });
    } else
      return true;

  }, [isLoggedInSetup, setupStates, accountColor, router]);

  const setSearchParams = useCallback(async (paramsToUpdate: Record<string, string>) => {
    if (!(typeof window !== 'undefined')) return; // Is not browser
    const params = new URLSearchParams(window.location.search);
    setErrorMessage(null);
    for (const [key, value] of Object.entries(paramsToUpdate)) {
      let success = true;
      if (key === 'step') {
        switch (value) {
          case '0':
            // router.push(isLoggedInSetup ? `/app/start` : '/start', { scroll: false });
            success = true;
            break;
          case '1':
            success = await handleStepOne();
            break;
          case '2':
            success = await handleStepTwo();
            break;
          default:
            break;
        }
      }
      if (!success) {
        return;
      }
      params.set(key, value);
    }
    router.push(`?${params.toString()}`, { scroll: false });
    setIsLoading(false);
  }, [router, handleStepOne, handleStepTwo]);

  const handlePlanSelected = useCallback((plan: any) => {
    setSetupStates(prevState => ({ ...prevState, selectedPlan: plan }));
    //NOTE: Uncomment this to enable Chargebee payment form, instead of Checkout popup
    // setSearchParams({ step: '2' })

    let planId = `plan-${plan.id}-${plan.price[setupStates.activePeriod == "Yearly" ? "YearlyTotal" : "Monthly"]}`;
    if (setupStates.activePeriod == "Monthly") {
      planId = `plan-first-month-${plan.firstMonthMonthlyPrice}`;
    }
    console.info("planId: ", planId);
    console.info(`setSelectedPlan: `, plan, setupStates.activePeriod);

    // Track InitiateCheckout for Meta Pixel with eventId for deduplication
    const checkoutEventId = generateEventId('initiatecheckout', user?.id);
    trackMetaEvent('InitiateCheckout', {
      content_name: planId,
      content_category: 'subscription',
      value: plan.price[setupStates.activePeriod === "Yearly" ? "YearlyTotal" : "Monthly"],
      currency: 'USD'
    }, checkoutEventId);

    openChargebeeCheckout(planId)
  }, [router, cbInstance, setupStates.activePeriod, setupStates.selectedPlan]);

  const handlePeriodSelected = useCallback((period: any) => {
    setSetupStates(prevState => ({ ...prevState, activePeriod: period }));
  }, [router]);

  const Step1 = useMemo(() => (
    <div className='mt-8 font-dashboard animate-fade-in-up-short'>

      <div className={classNames("space-y-3", isLoggedInSetup ? 'text-center' : 'text-center sm:text-left')}>
        <h1 className="mt-10 mb-3 text-2xl font-semibold text-center text-slate-900 sm:text-3xl lg:mt-5 xl:mt-7 xl:text-2xl">
          Get started for free
        </h1>
        <p className={classNames("mx-auto text-sm leading-6 text-gray-500 text-center", isLoggedInSetup ? "max-w-sm" : "max-w-md")}>
          Connect your Instagram account to Keesy and <br className='hidden lg:inline' />start your growth
        </p>
      </div>

      <div className="relative my-8">
        <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="instagram-username">Your Instagram Username</label>
        <InstagramSearchField
          // @ts-ignore:next-line
          id="instagram-username"
          disableSearch={true}
          disabled={isLoading}
          defaultValue={setupStates.instagramUsername}
          onValueChange={(value) => {
            setSetupStates(prevState => ({ ...prevState, instagramUsername: value }))
          }}
          onEnterKeyPressed={() => {
            setSearchParams({ step: '1' })
          }} />

        <div className="flex items-center mt-4 text-sm text-gray-500">
          <BiCheckShield className="w-5 h-5 mr-1 text-[#4faf02]" />
          <span>We never ask for your Instagram password</span>
        </div>

        {/* <ColorPicker className={`${isLoggedInSetup ? 'mt-5' : 'mt-7'} mb-9`} isOnSetupPage={true} onColorChange={(color) => setAccountColor(color)} disabled={isLoading} /> */}
      </div>
      {isLoading ? (
        <Button type="submit" authButton={true} disabled className="flex justify-center rounded-full w-full text-shadow px-4 py-3.5 text-md font-medium text-white bg-yellow-300 border border-transparent shadow-sm cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
          style={{
            background: `linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(275.25deg, #460EE7 0.81%, #7C52F5 54.05%, #460EE7 99.4%)`,
            boxShadow: `0px 1px 2px rgba(47, 10, 154, 0.4), 0px 0px 0px 1px rgba(117, 73, 244, 0.8)`,
          }}
        >
          <LoadingSpinner className="w-4 h-4 mr-1 brightness-[5]" />
          Loading...
        </Button>
      ) : (
          <Button type="submit"
            authButton={true}
            className="flex justify-center rounded-full w-full text-shadow px-4 py-3.5 text-md font-medium text-white border border-transparent shadow-sm focus:outline-none focus:ring-2"
            style={{
              background: `linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(275.25deg, #460EE7 0.81%, #7C52F5 54.05%, #460EE7 99.4%)`,
              boxShadow: `0px 1px 2px rgba(47, 10, 154, 0.4), 0px 0px 0px 1px rgba(117, 73, 244, 0.8)`,
            }}
          onClick={() => {
            setSearchParams({ step: '1' })
            // Track event
            // track('Started Setup', {
            //   username: setupStates.instagramUsername,
            //   color: accountColor.name,
            // })
          }}>
          Start your growth
          <HiArrowSmRight className="w-5 h-5 place-self-center" />
        </Button>
      )}

      <span className="text-sm inline-flex items-center font-semibold mt-5 w-full md:mt-6 place-content-center tracking-tight text-transparent bg-gradient-to-br !bg-clip-text saturate-[1.3]"
        style={{ background: 'linear-gradient(74.41deg, rgb(67, 153, 23) -52.09%, rgb(176, 231, 82) 170.9%)' }}
      >
        <LockClosedIcon className="mr-1 h-[0.85rem] w-[0.85rem] !text-[#4faf02]" />
        <span className="">SSL Encrypted</span>
      </span>
    </div>
  ), [router, setupStates.instagramUsername, setupStates.email, accountColor, isLoading, step]);

  const Step2 = useMemo(() => (
    isLoggedInSetup ? (
      <div className='!mt-0 font-dashboard'>
        <Pricing isSetupProcess={true} selectedPeriod={setupStates.activePeriod} />
      </div >
    ) : (
        <div className='relative animate-fade-in-up-short'>

        <div className={classNames("space-y-3 font-dashboard", isLoggedInSetup ? 'text-center' : 'text-center sm:text-left')}>
            {/* {!isLoggedInSetup && (
            <Link title="Back" className="flex items-center gap-2 pb-2 group font-dashboard"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setSearchParams({ step: (step - 1).toString() })
              }}
            >
              <FiArrowLeft className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
              <span className="text-gray-500 group-hover:text-gray-700">Back</span>
            </Link>
          )} */}

            <h1 className="mt-10 mb-3 text-2xl font-semibold text-center text-slate-900 sm:text-3xl lg:mt-5 xl:mt-7 xl:text-2xl">
            {(isLoggedInSetup ? "Start your growth for free!" : "Create your account")}
          </h1>
            <p className={classNames("mx-auto text-sm leading-6 text-gray-500 text-center", isLoggedInSetup ? "max-w-sm" : "max-w-md")}>
            {(isLoggedInSetup ?
              (<>
                All plans have our <span className='font-medium bg-[linear-gradient(90deg,transparent_0%,#ffdc59)] py-1 pr-2 leading-normal sm:leading-[41px] saturate-[1.2] transition-all duration-200 ease-in-out hover:bg-[linear-gradient(90deg,transparent_-20%,#ffdc59)] hover:text-black hover:saturate-[2] pl-0 hover:pl-2'>
                  100% growth guarantee</span>
                <br />
                You can <span className="font-medium">cancel for free at anytime</span>.

                {/* TODO: IG Account Preview */}

              </>)
              : (<>
                  <span className='-mb-2 text-sm leading-6 text-gray-500'>
                  Sign up & start your Instagram growth journey.{" "}
                  <br className='hidden sm:block' />
                  You'll also receive our{" "}
                    {/* <span className='font-medium text-gray-500 bg-[linear-gradient(90deg,transparent_0%,#ffdc59)] py-1 pr-2 leading-normal sm:leading-[41px] saturate-[1.2] transition-all duration-200 ease-in-out hover:bg-[linear-gradient(90deg,transparent_-20%,#ffdc59)] hover:text-black hover:saturate-[2] pl-0 hover:pl-2'> */}
                    100% growth guarantee
                    {/* </span> */}
                </span>
              </>)
            )}
          </p>
        </div>

          <label className="block font-medium text-gray-700 text-sm mb-1.5 mt-5" htmlFor="email">Email</label>
        <div className="relative mt-1">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
            <svg className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-5 top-1/2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
            <input className="block w-full rounded-[9px] py-2.5 px-4 pl-[3.2rem] text-[1.03rem] disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100 ring-1 !focus:ring-0 border-transparent border-0 focus:ring-offset-[-1px] focus:shadow-[0_0px_0px_3px_rgba(110,63,243,0.12)] focus:border-[#7549f4] focus:ring-[#7549f4] shadow-xs ring-neutral-200 placeholder-neutral-400"
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email"
            disabled={isLoading}
            onChange={(event) => {
              setSetupStates(prevState => ({ ...prevState, email: event.target.value }))
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter')
                setSearchParams({ step: '2' })
            }}
            required
            autoComplete={'email'}
            autoCapitalize="off"
            defaultValue={setupStates.email}
          />
        </div>
          <label className="block font-medium text-gray-700 text-sm mb-1.5 mt-5" htmlFor="password">Password</label>
          <div className="relative mt-1 mb-5">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
            <svg className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-5 top-1/2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
            <input className="block w-full rounded-[9px] py-2.5 px-4 pl-[3.2rem] text-[1.03rem] disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100 ring-1 !focus:ring-0 border-transparent border-0 focus:ring-offset-[-1px] focus:shadow-[0_0px_0px_3px_rgba(110,63,243,0.12)] focus:border-[#7549f4] focus:ring-[#7549f4] shadow-xs ring-neutral-200 placeholder-neutral-400"
            id="password"
            name="password"
            type="password"
            autoComplete={"password"}
            placeholder="Enter your password"
            disabled={isLoading}
            onChange={(event) => {
              setSetupStates(prevState => ({ ...prevState, password: event.target.value }))
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter')
                setSearchParams({ step: '2' })
            }}
            required
            autoCapitalize="off"
          />
        </div>
        {isLoading ? (
            <Button type="submit" disabled authButton={true} className="flex justify-center rounded-full w-full text-shadow px-4 py-3.5 text-md font-medium text-white bg-yellow-300 border border-transparent shadow-sm cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              style={{
                background: `linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(275.25deg, #460EE7 0.81%, #7C52F5 54.05%, #460EE7 99.4%)`,
                boxShadow: `0px 1px 2px rgba(47, 10, 154, 0.4), 0px 0px 0px 1px rgba(117, 73, 244, 0.8)`,
              }}
            >
              <LoadingSpinner className="w-4 h-4 mr-1 brightness-[5]" />
            Activating account...
          </Button>
        ) : (
              <Button type="submit"
                authButton={true}
                className="flex justify-center rounded-full w-full text-shadow px-4 py-3.5 text-md font-medium text-white bg-yellow-300 border border-transparent shadow-sm cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                style={{
                  background: `linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(275.25deg, #460EE7 0.81%, #7C52F5 54.05%, #460EE7 99.4%)`,
                  boxShadow: `0px 1px 2px rgba(47, 10, 154, 0.4), 0px 0px 0px 1px rgba(117, 73, 244, 0.8)`,
                }}
            onClick={() => {
              setSearchParams({ step: '2' })
              // Track event
              track('Signed Up', {
                username: setupStates.instagramUsername,
                email: setupStates.email,
              })
            }}>
            Activate your account
            <HiArrowSmRight className="w-5 h-5 place-self-center" />
          </Button>
        )}

          <span className="text-sm inline-flex items-center font-semibold mt-5 w-full md:mt-6 place-content-center tracking-tight text-transparent bg-gradient-to-br !bg-clip-text saturate-[1.3]"
            style={{ background: 'linear-gradient(74.41deg, rgb(67, 153, 23) -52.09%, rgb(176, 231, 82) 170.9%)' }}
          >
            <LockClosedIcon className="mr-1 h-[0.85rem] w-[0.85rem] !text-[#4faf02]" />
            <span className="">SSL Encrypted</span>
          </span>
      </div>
    )
  ), [router, setupStates.activePeriod, setupStates.instagramUsername, setupStates.email, setupStates.password, accountColor, isLoading, step]);


  const Step3 = cbInstance ? (
    <div className='relative animate-fade-in-up-short'>
      <div className={classNames("space-y-3 font-dashboard", isLoggedInSetup ? 'text-center' : 'text-center sm:text-left')}>

        {/* Hide back button */}
        {/* {!isLoggedInSetup && (
          <Link href="#" title="Back" className="flex items-center gap-2 pb-2 group font-dashboard"
            onClick={(e) => {
              e.preventDefault()
              setSearchParams({ step: (step - 1).toString() })
            }}
          >
            <FiArrowLeft className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
            <span className="text-gray-500 group-hover:text-gray-700">Back</span>
          </Link>
        )} */}

        <h1 className="mt-10 mb-3 text-2xl font-semibold text-center text-slate-900 sm:text-3xl lg:mt-5 xl:mt-7 xl:text-2xl">
          {isLoggedInSetup && (
            <div className="flex flex-col items-center justify-center">
              <LightningFillIcon className="inline w-8 h-8 mb-2 text-orange-500 align-middle" />
              {/* Start your Insta growth */}
              {/* Activate your growth */}
              Guaranteed growth <br />(or it's 100% free!)
            </div>
          )}
        </h1>
        <p className={classNames("mx-auto text-sm text-gray-500", isLoggedInSetup ? "max-w-sm" : "max-w-md")}>
          {isLoggedInSetup && (
            <>
              {/* Almost there! <br /> */}
              {/* You will <span className='font-semibold'>not</span> be charged today. <br />
              You can cancel your trial <span className='font-semibold'>for free</span> at anytime. */}
              Start your Instagram growth for <span className="font-semibold">free</span>.
              <br />
              No contracts. No gimmicks. Cancel anytime.
            </>
          )}
        </p>
      </div>

      <ChargebeePaymentForm
        selectedPlan={setupStates.selectedPlan}
        selectedYearly={setupStates.activePeriod == "Yearly"}
        selectedUsername={setupStates.instagramUsername}
        accountColor={accountColor.name}
        isFromSetup={true}
        userCountry={userCountry}
      />
    </div>
  ) : null;

  return (
    <div className={classNames("container block items-center h-full mx-auto overflow-visible text-left",
      isLoggedInSetup && 'px-5',
      // step == 0 ? (isLoggedInSetup ? 'max-w-md' : 'max-w-sm') : step == 2 ? 'max-w-lg' : 'max-w-7xl'
    )}>
      <div className={classNames(`absolute left-0 h-[7px] mx-auto bg-gray-200 md:bg-inherit md:bg-gradient-to-r from-gray-200 via-[var(--color-gray-200)_85%] to-white w-full top-0`)}>
        <div className="h-[7px] rounded-tr-full rounded-br-full transition-all duration-300 ease-in-out"
          style={{
            background: 'linear-gradient(90deg, #7549F4 0%, #F94DDB 50%, #F3CA9E 100%)',
            width: isLoggedInSetup
              ? `${((step + 1) * 25).toString()}%`
              : `${((step + 1) * 33).toString()}%`
          }} />
      </div>
      {/* <div className='items-center mx-auto text-center'>
        <LoadingSpinner className="w-8 h-8 !mx-auto text-purple-500" />
        <p className="mt-3 font-medium text-purple-500 text-md">Logging you in ...</p>
      </div> */}

      {/* Debugging */}
      {/* <div className='absolute bottom-0 right-0 p-4 bg-blue-100 rounded-md shadow-lg w-[600px] z-10'>
        <h2 className='text-lg font-bold text-blue-800'>Debugging Information:</h2>
        <ul className='mt-2 text-blue-700 list-disc list-inside'>
          <li><b>Current Step:</b> {step}</li>
          <li><b>Instagram Username:</b> {setupStates.instagramUsername}</li>
          <li><b>Selected Period:</b> {JSON.stringify(activePeriod)}</li>
          <li><b>Selected Plan:</b> {JSON.stringify(selectedPlan)}</li>
        </ul>
      </div> */}


      <div className="my-5 lg:my-10">

        {errorMessage && (
          <div className="relative animate-fade-in-up-short px-4 py-2.5 border-1 rounded-[10px]" role="alert" style={{ background: 'rgba(223, 28, 65, 0.08)', color: '#DF1C41', borderColor: 'rgba(223, 28, 65, 0.08)' }}>
            <svg className="inline-flex mr-2.5 align-middle" width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 6.00003V8.6667M8 11.3334H8.00667M7.07688 2.59451L1.59362 12.0656C1.28948 12.5909 1.13742 12.8536 1.15989 13.0692C1.1795 13.2572 1.27801 13.4281 1.43091 13.5392C1.60622 13.6667 1.90973 13.6667 2.51674 13.6667H13.4833C14.0903 13.6667 14.3938 13.6667 14.5691 13.5392C14.722 13.4281 14.8205 13.2572 14.8401 13.0692C14.8626 12.8536 14.7105 12.5909 14.4064 12.0656L8.92312 2.59451C8.62007 2.07107 8.46855 1.80935 8.27087 1.72145C8.09843 1.64477 7.90157 1.64477 7.72913 1.72145C7.53145 1.80935 7.37992 2.07107 7.07688 2.59451Z" stroke="#DF1C41" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="block font-medium align-middle sm:inline" dangerouslySetInnerHTML={{ __html: errorMessage }}></span>

            <span className="absolute top-0 bottom-0 right-0 px-4 py-3">
              <svg className="w-5 h-5 align-middle fill-current" width="16" height="16" role="button" viewBox="0 0 16 16" fill="none" onClick={() => setErrorMessage(null)} xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.8">
                  <path d="M11.3332 4.66663L4.6665 11.3333M4.6665 4.66663L11.3332 11.3333" stroke="#DF1C41" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </g>
              </svg>
            </span>
          </div>
        )}

        {/* Debugging */}

        {/* User: {JSON.stringify(user)}<br /> */}
        {/* isLoggedin: {isLoggedIn ? 'true' : 'false'}<br /> */}
        {/* Step: {step}<br /> */}
        {/* Email: {setupStates.email}<br />
        Password: {setupStates.password}<br /> */}
        {/* isLoggedInSetup: {isLoggedInSetup ? 'true' : 'false'}<br /> */}
        {/* Selected username: {setupStates.instagramUsername}<br /> */}
        {/* Selected period: {JSON.stringify(setupStates.activePeriod)}<br />
        Selected plan: {JSON.stringify(setupStates.selectedPlan)}<br /> */}
        {/* Selected color: {JSON.stringify(accountColor)}<br />



        {/* TODO: Uncomment this to enable step transitions */}
        <SetupLayoutContext.Provider value={{ instagramUsername: setupStates.instagramUsername, accountColor, activePeriod: setupStates.activePeriod, setActivePeriod: handlePeriodSelected, selectedPlan: setupStates.selectedPlan, setSelectedPlan: handlePlanSelected, isLoading, setIsLoading, setErrorMessage }}>
          {/* <AnimatePresence mode='wait'>
            {step === 0 && (
              <motion.div key="step1" variants={variants} initial="initial" animate="enter" exit="exit">{Step1}</motion.div>
            )}
            {step === 1 && (
              <motion.div key="step2" variants={variants} initial="initial" animate="enter" exit="exit">{Step2}</motion.div>
            )}
            {step === 2 && (
              <motion.div key="step3" variants={variants} initial="initial" animate="enter" exit="exit">{Step3}</motion.div>
            )}
          </AnimatePresence> */}
          {step === 0 && Step1}
          {step === 1 && Step2}
          {step === 2 && Step3}
        </SetupLayoutContext.Provider>

        {/* Load Chargebee.js */}
        <Script src="https://js.chargebee.com/v2/chargebee.js" strategy="afterInteractive"
          onLoad={() => { console.log("CB has loaded") }}
        />
        {/* <Script id="chargebee-config" strategy="afterInteractive">
          {`
              var waitForChargebee = setInterval(function () {
                if (typeof Chargebee != 'undefined') { 
                  window.chargebeeInstance = Chargebee.init({
                    site: "${chargebeeSite}",
                    publishableKey: "${chargebeeFrontendSettingsForSite(chargebeeSite).publishableKey}",
                    iFrameOnly: true,
                  });
                  window.Chargebee = Chargebee;
                  clearInterval(waitForChargebee);
                }
              }, 50);
            `}
        </Script> */}

      </div>
    </div>
  );
}
